import { useState, useEffect } from 'react';
import axios from 'axios';
import { providersApi, ProviderWithUser } from '../services/providers';
import { useAuth } from './useAuth';
import { getApiErrorMessage } from '../utils/errors';

export const useProvider = (providerId: string | undefined) => {
  const [provider, setProvider] = useState<ProviderWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!providerId) {
      setLoading(false);
      return;
    }

    const fetchProvider = async () => {
      try {
        setLoading(true);
        const data = await providersApi.getProvider(parseInt(providerId));
        setProvider(data);
        setError(null);
      } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
          setError(getApiErrorMessage(err, 'Failed to fetch provider'));
        } else if (err instanceof Error) {
          setError(err.message || 'An unexpected error occurred');
        } else {
          setError('An unexpected error occurred');
        }
        console.error('Error fetching provider:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProvider();
  }, [providerId]);

  return { provider, loading, error };
};

export const useCurrentProvider = () => {
  const { user } = useAuth();
  const [provider, setProvider] = useState<ProviderWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchCurrentProvider = async () => {
      try {
        setLoading(true);
        const data = await providersApi.getCurrentProvider();
        setProvider(data);
        setError(null);
      } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 404) {
            setProvider(null);
            setError(null);
          } else {
            setError(getApiErrorMessage(err, 'Failed to fetch provider profile'));
          }
        } else if (err instanceof Error) {
          setError(err.message || 'An unexpected error occurred');
        } else {
          setError('An unexpected error occurred');
        }
        console.error('Error fetching current provider:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentProvider();
  }, [user]);

  return { provider, loading, error };
};
